/**
 * Wires the API client's global 401 path to the router and the query cache
 * (`050-auth.md` §"What happens on a 401", `111-frontend-architecture.md` §8.2).
 *
 * It is a component rather than a module-level import because `client.ts` may not
 * import the router or the QueryClient — that is a cycle, and it is also what makes
 * the client testable without either.
 *
 * THE RULE ON THIS PATH: a 401 must not lose data.
 *
 *   - It routes to /login **carrying the current location**, so the user lands back
 *     where they were.
 *   - It does NOT wipe the query cache. 050-auth.md is explicit: the cache is not
 *     wiped on the way out — nothing is in it that is not already on screen — it is
 *     invalidated on the way back IN, after a successful re-login, because the data
 *     may have changed while the session was dead. Clearing here would blank out the
 *     page the user was reading behind the login screen, which reads as data loss
 *     even though the rows are still on the server.
 *   - From M2, IndexedDB (drafts, outbox, pendingUploads) is never touched by any
 *     auth path, and the outbox is PAUSED, not drained. An implementation that calls
 *     `outbox.clear()` here violates the project's first design value and a review
 *     must reject it on sight.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { setUnauthenticatedHandler } from '@/api/client';
import { keys } from '@/api/keys';
import { toast } from '@/components/ui/sonner';
import { translate } from '@/lib/i18n';
import { outbox } from '@/offline/outbox';

export function SessionBridge({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    setUnauthenticatedHandler(() => {
      // Entries STAY in IndexedDB, in order, with their retry count untouched. A 401 is a
      // failure of the session, not of the mutation (`112-offline-drafts.md` §9).
      //
      // The scenario, spelled out: your session expires overnight. In the morning you type
      // three paragraphs and the autosave 401s. The naive implementation logs you out,
      // clears local state, and YOUR THREE PARAGRAPHS ARE GONE. This one queues them,
      // pauses, asks you to sign in, and lands them. You never find out anything happened.
      outbox.pause('unauthenticated');

      // Only the session itself is dropped. Everything else stays cached and is
      // invalidated after re-login (see useSession's login mutation).
      queryClient.setQueryData(keys.session(), null);

      const { pathname, search } = window.location;
      if (pathname !== '/login') {
        const next = encodeURIComponent(pathname + search);
        void navigate(`/login?next=${next}`, { replace: true });
        toast.warning(translate('auth.session.expired'));
      }
    });

    return () => {
      setUnauthenticatedHandler(() => {});
    };
  }, [navigate, queryClient]);

  return children;
}
